/**
 * Fallback Toughkey host callbacks (sync) before Angular registers the demo modal bridge.
 */
export function installToughKeyWindowCallbacks(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const w = window as unknown as Record<string, unknown>;

  function deviceIndexStr(deviceIndex: unknown): string {
    if (deviceIndex == null) {
      return '0';
    }
    if (typeof deviceIndex === 'object' && deviceIndex !== null && 'toString' in deviceIndex) {
      return String((deviceIndex as { toString: () => string }).toString());
    }
    return String(deviceIndex);
  }

  const syncInsert = function (_deviceType: unknown, deviceIndex: unknown): string {
    const names: Record<string, string> = {
      '0': 'Primary Administrative',
      '1': 'Secondary',
      '2': 'Tertiary',
    };
    const idx = deviceIndexStr(deviceIndex);
    const label = names[idx] ?? 'Unknown';
    if (
      !window.confirm(
        `Please insert the ${label} Toughkey fully into USB.\n\nClick OK only after it is seated (LED may flash).`
      )
    ) {
      return 'cancel';
    }
    return window.confirm(
      'Wait 3–5 seconds for Windows to recognize the device.\n\nClick OK when ready to initialize.'
    )
      ? 'ok'
      : 'cancel';
  };

  w.promptInsertKey = function (t: unknown, i: unknown): string | Promise<string> {
    const bridge = window.__toughKeyModalBridge;
    if (bridge) {
      return bridge.promptInsertKey(t, i);
    }
    return syncInsert(t, i);
  };

  w.promptRemoveDevice = function (t: unknown, i: unknown): string | Promise<string> {
    const bridge = window.__toughKeyModalBridge;
    if (bridge) {
      return bridge.promptRemoveDevice(t, i);
    }
    const idx = deviceIndexStr(i);
    return window.confirm(`Device initialized (${idx}). Click OK to continue.`) ? 'ok' : 'cancel';
  };

  w.promptNoKeyReuse = function (t: unknown, i: unknown): string | Promise<string> {
    const bridge = window.__toughKeyModalBridge;
    if (bridge) {
      return bridge.promptNoKeyReuse(t, i);
    }
    return window.confirm('Duplicate device. Insert a different Toughkey. OK when ready.')
      ? 'ok'
      : 'cancel';
  };

  w.warnAlreadyInitialized = function (): string | Promise<string> {
    const bridge = window.__toughKeyModalBridge;
    if (bridge) {
      return bridge.warnAlreadyInitialized();
    }
    return window.confirm('Device already initialized. Re-init erases data. Continue?')
      ? 'continue'
      : 'cancel';
  };

  w.promptRemoveAllKeys = function (): string | Promise<string> {
    const bridge = window.__toughKeyModalBridge;
    if (bridge) {
      return bridge.promptRemoveAllKeys();
    }
    return window.confirm(
      'Remove all Toughkeys from USB before initialization. Click OK when ready.'
    )
      ? 'ok'
      : 'cancel';
  };

  w.enrollSecrets = function (): string {
    console.log('[ToughKey] enrollSecrets');
    return 'OK';
  };

  w.updateSetupStatus = function (title: unknown, message: unknown): string {
    console.log('[ToughKey][SETUP]', String(title), '-', String(message));
    return 'OK';
  };

  console.log('[ToughKey] Window callbacks ready (pre-Angular)');
}

declare global {
  interface Window {
    __toughKeyModalBridge?: import('./app/services/tough-key-progress-modal.service').ToughKeyModalBridge;
  }
}
